/**
 * Hub MP3 playlist: Functions mint signed URLs; Storage streams the bytes.
 */
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import { doc, setDoc, updateDoc, deleteDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db, storage, functions, isFunctionsAvailable, isStorageAvailable, isFirestoreAvailable } from '../../../firebase';
import {
  HUB_AUDIO_COLLECTION,
  HUB_AUDIO_STORAGE_PREFIX,
  HUB_AUDIO_MAX_BYTES,
  isAllowedHubAudioType,
} from './mp3PlayerEngine';

function requireFunctions() {
  if (!isFunctionsAvailable() || !functions) {
    throw new Error('Cloud Functions not initialized.');
  }
  return functions;
}

function newTrackId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `track-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function titleFromFileName(fileName) {
  return String(fileName || 'Track').replace(/\.[^.]+$/, '') || 'Track';
}

/**
 * Probe duration from a local file. Returns 0 if metadata cannot be read.
 * @param {Blob} audioFile
 * @returns {Promise<number>}
 */
export function probeAudioDuration(audioFile) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return Promise.resolve(0);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(audioFile);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const finish = (sec) => {
      URL.revokeObjectURL(url);
      resolve(sec);
    };
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      finish(Number.isFinite(d) ? Math.round(d) : 0);
    };
    audio.onerror = () => finish(0);
    audio.src = url;
  });
}

/**
 * @returns {Promise<Array<{
 *   id: string,
 *   title: string,
 *   storagePath: string,
 *   contentType: string,
 *   durationSec: number,
 *   sortOrder: number,
 *   uploadedBy: string,
 *   createdAt: number,
 * }>>}
 */
export async function listHubAudioTracks() {
  const fns = requireFunctions();
  const call = httpsCallable(fns, 'listHubAudioTracks');
  const res = await call();
  return Array.isArray(res?.data?.tracks) ? res.data.tracks : [];
}

/**
 * @param {string} trackId
 * @returns {Promise<{ url: string, expiresAt: number, trackId: string }>}
 */
export async function getHubAudioStreamUrl(trackId) {
  const id = String(trackId || '').trim();
  if (!id) throw new Error('Missing track.');
  const fns = requireFunctions();
  const call = httpsCallable(fns, 'getHubAudioStreamUrl');
  const res = await call({ trackId: id });
  const url = typeof res?.data?.url === 'string' ? res.data.url : '';
  const expiresAt = Number(res?.data?.expiresAt) || 0;
  if (!url) throw new Error('No stream URL returned.');
  return { url, expiresAt, trackId: res?.data?.trackId || id };
}

/**
 * Admin upload: Storage object (private) + Firestore metadata (no download URL).
 * @param {Blob|File} audioFile
 * @param {{ uid: string, sortOrder?: number }} opts
 */
export async function uploadHubAudioTrack(audioFile, { uid, sortOrder = 0 } = {}) {
  if (!isStorageAvailable() || !storage) {
    throw new Error('Firebase Storage not initialized.');
  }
  if (!isFirestoreAvailable() || !db) {
    throw new Error('Firestore not initialized.');
  }
  if (!(audioFile instanceof Blob)) {
    throw new Error('Invalid audio file.');
  }
  if (audioFile.size > HUB_AUDIO_MAX_BYTES) {
    throw new Error('Audio file must be under 20MB.');
  }
  const fileName =
    (audioFile instanceof File && audioFile.name)
      ? audioFile.name
      : `track-${Date.now()}.mp3`;
  if (!isAllowedHubAudioType(audioFile.type, fileName)) {
    throw new Error('Use mp3, m4a, aac, ogg, or wav.');
  }
  const owner = String(uid || '').trim();
  if (!owner) throw new Error('Not signed in.');

  const trackId = newTrackId();
  const storagePath = `${HUB_AUDIO_STORAGE_PREFIX}/${trackId}/${fileName}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, audioFile, {
    contentType: audioFile.type || 'audio/mpeg',
  });

  const durationSec = await probeAudioDuration(audioFile);
  const payload = {
    title: titleFromFileName(fileName),
    storagePath,
    contentType: audioFile.type || 'audio/mpeg',
    durationSec,
    sortOrder: Number(sortOrder) || 0,
    uploadedBy: owner,
    createdAt: serverTimestamp(),
  };
  await setDoc(doc(db, HUB_AUDIO_COLLECTION, trackId), payload);

  return {
    id: trackId,
    title: payload.title,
    storagePath,
    contentType: payload.contentType,
    durationSec,
    sortOrder: payload.sortOrder,
    uploadedBy: owner,
    createdAt: Date.now(),
  };
}

/**
 * Admin rename: updates Firestore title only (Storage path unchanged).
 * @param {string} trackId
 * @param {string} title
 */
export async function renameHubAudioTrack(trackId, title) {
  if (!isFirestoreAvailable() || !db) {
    throw new Error('Firestore not initialized.');
  }
  const id = String(trackId || '').trim();
  if (!id) throw new Error('Missing track.');
  const next = String(title || '').trim();
  if (!next) throw new Error('Title required.');
  if (next.length > 120) throw new Error('Title must be under 120 characters.');
  await updateDoc(doc(db, HUB_AUDIO_COLLECTION, id), { title: next });
  return { id, title: next };
}

/**
 * Admin assign / clear Radio credit on a hub track.
 * @param {string} trackId
 * @param {{ id: string, name: string, slug: string } | null} artist
 */
export async function assignHubAudioArtist(trackId, artist) {
  if (!isFirestoreAvailable() || !db) {
    throw new Error('Firestore not initialized.');
  }
  const id = String(trackId || '').trim();
  if (!id) throw new Error('Missing track.');
  if (!artist) {
    await updateDoc(doc(db, HUB_AUDIO_COLLECTION, id), {
      artistId: deleteField(),
      artistName: deleteField(),
      artistSlug: deleteField(),
    });
    return { id, artistId: '', artistName: '', artistSlug: '' };
  }
  const artistId = String(artist.id || '').trim();
  const artistName = String(artist.name || '').trim();
  const artistSlug = String(artist.slug || '').trim();
  if (!artistId || !artistName || !artistSlug) {
    throw new Error('Pick an artist.');
  }
  await updateDoc(doc(db, HUB_AUDIO_COLLECTION, id), {
    artistId,
    artistName,
    artistSlug,
  });
  return { id, artistId, artistName, artistSlug };
}

/**
 * @param {{ id: string, storagePath?: string }} track
 */
export async function deleteHubAudioTrack(track) {
  const id = String(track?.id || '').trim();
  if (!id) throw new Error('Missing track.');
  if (isFirestoreAvailable() && db) {
    await deleteDoc(doc(db, HUB_AUDIO_COLLECTION, id));
  }
  const storagePath = String(track?.storagePath || '').trim();
  if (storagePath && isStorageAvailable() && storage) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      const code = err && err.code;
      if (code !== 'storage/object-not-found') throw err;
    }
  }
}
